import { keysToCamel } from '~/utility/toCamel'
import { BookingPaymentStatus, bookingPaymentStatusSchema } from './schema'

export class BookingPaymentStatusServiceFetch {
  async get(
    paymentId: string,
    bookingId: string,
  ): Promise<BookingPaymentStatus> {
    const url = `/_/fallback/booking/payment/${paymentId}/${bookingId}/?ajax`

    try {
      const response = await $fetch(url, {
        timeout: 10000,
      })

      // Check if response is a string and try to parse it
      let parsedResponse = response
      if (typeof response === 'string') {
        try {
          parsedResponse = JSON.parse(response)
        } catch (parseError: any) {
          console.error(
            'Failed to parse JSON response for booking payment status',
            {
              error: parseError,
              url,
              status: 200,
              responseType: typeof response,
              responseContent: response,
              paymentId,
              bookingId,
            },
          )
          throw new Error(`Invalid JSON response: ${parseError.message}`)
        }
      }

      // Ensure we have an object before using keysToCamel
      if (typeof parsedResponse !== 'object' || parsedResponse === null) {
        console.error('Invalid response type for booking payment status', {
          url,
          status: 200,
          responseType: typeof parsedResponse,
          responseContent: parsedResponse,
          paymentId,
          bookingId,
        })
        throw new Error(
          `Expected object response, got ${typeof parsedResponse}`,
        )
      }

      const camelCasedResponse = keysToCamel(parsedResponse)

      try {
        return bookingPaymentStatusSchema.parse(camelCasedResponse)
      } catch (zodError) {
        // Log full details when Zod validation fails
        console.error('Zod validation failed for booking payment status', {
          error: zodError,
          url,
          status: 200,
          responseType: typeof parsedResponse,
          responseContent: camelCasedResponse,
          rawResponse: parsedResponse,
          originalResponse: response,
          paymentId,
          bookingId,
        })
        throw zodError
      }
    } catch (fetchError: any) {
      // Extract status code from various error formats
      const statusCode =
        fetchError?.status ||
        fetchError?.statusCode ||
        fetchError?.data?.statusCode ||
        fetchError?.response?.status ||
        fetchError?.statusCode
      // Check if it's a 404 error
      if (statusCode === 404) {
        console.error('Booking payment status not found (404)', {
          error: fetchError,
          url,
          paymentId,
          bookingId,
          statusCode,
        })
        // Throw a specific error for 404
        const notFoundError = new Error('Booking payment status not found')
        ;(notFoundError as any).statusCode = 404
        ;(notFoundError as any).originalError = fetchError
        throw notFoundError
      }

      // Check for timeout errors
      const isTimeout =
        fetchError?.message?.toLowerCase().includes('timeout') ||
        fetchError?.code === 'ECONNABORTED' ||
        fetchError?.code === 'ETIMEDOUT'

      // Log other fetch errors with full context
      const errorDetails = {
        message: fetchError?.message || 'Unknown error',
        statusCode,
        url,
        paymentId,
        bookingId,
        errorType: fetchError?.name || 'FetchError',
        isTimeout,
        stack: fetchError?.stack,
      }

      console.error('Failed to fetch booking payment status', errorDetails)

      // Enhance the error with additional context before rethrowing
      if (fetchError instanceof Error) {
        ;(fetchError as any).context = errorDetails
      }

      throw fetchError
    }
  }
}
